"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import Search from "lucide-react/dist/esm/icons/search";
import LayoutGrid from "lucide-react/dist/esm/icons/layout-grid";
import Globe from "lucide-react/dist/esm/icons/globe";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import { AssetThumb } from "@/components/asset/AssetThumb";
import { EmptyState } from "@/components/ui/empty-state";
import { useStudioExplore } from "@/hooks/studio";
import { cn } from "@/lib/utils";
import { logEvent } from "@/lib/events";

const TAG_FILTERS = ["All", "Battle Royale", "Zone Wars", "Box Fight", "Prop Hunt", "Deathrun", "Creative", "Puzzle", "Survival", "Racing", "Roleplay"];

export function StudioExplorePageContent() {
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("All");

  useEffect(() => {
    logEvent("studio_explore_viewed", undefined, { severity: "low" });
  }, []);

  const { data: rawProjects, isLoading } = useStudioExplore({ search, tag: tagFilter === "All" ? undefined : tagFilter });
  const projects = (rawProjects as unknown) as Array<{
    id: string;
    name: string;
    description: string | null;
    cover_image_url: string | null;
    tags: string[];
    status: string;
    updated_at: string;
    board_count?: number;
  }> | undefined;

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-5 w-5 text-primary" />
          <h1 className="font-display font-bold text-3xl">Explore Projects</h1>
        </div>
        <p className="text-muted-foreground">Discover public UEFN map projects from the community.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects…"
            aria-label="Search projects"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TAG_FILTERS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTagFilter(t)}
              className={cn(
                "px-3 py-1 text-xs rounded-full border transition-colors",
                tagFilter === t
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border/50 text-muted-foreground hover:border-primary/40"
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rounded-xl border border-border/50 overflow-hidden">
              <Skeleton className="h-32 w-full" />
              <div className="p-4 flex flex-col gap-2">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (projects ?? []).length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-border/50 bg-muted/10 py-16 text-center px-6">
          <LayoutGrid className="h-8 w-8 text-muted-foreground/30" />
          <div>
            <h3 className="font-display font-semibold text-lg">No public projects found</h3>
            <p className="text-muted-foreground text-sm mt-1">Be the first to publish a public project!</p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(projects ?? []).map((project) => (
            <Link
              key={project.id}
              href={`/studio/${project.id}/public`}
              prefetch
              className="group flex flex-col rounded-xl border border-border/50 bg-card hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all overflow-hidden"
            >
              <AssetThumb
                src={project.cover_image_url}
                alt={project.name}
                title={project.name}
                aspect="none"
                className="h-32"
                sizes="(max-width: 768px) 100vw, 33vw"
                fallbackClassName="text-3xl"
              >
                {project.cover_image_url && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                    <div className="absolute bottom-2 left-3">
                      <h3 className="font-display font-semibold text-sm text-white drop-shadow line-clamp-1">{project.name}</h3>
                    </div>
                  </>
                )}
              </AssetThumb>

              <div className="p-4 flex flex-col gap-2 flex-1">
                {!project.cover_image_url && (
                  <h3 className="font-display font-semibold text-sm">{project.name}</h3>
                )}
                {project.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{project.description}</p>
                )}
                <div className="flex items-center gap-2 mt-auto flex-wrap">
                  {(project.tags ?? []).slice(0, 2).map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-2xs">{tag}</Badge>
                  ))}
                  <span className="text-label text-muted-foreground ml-auto flex items-center gap-1">
                    <LayoutGrid className="h-3 w-3" />
                    {(project as { board_count?: number }).board_count ?? 0} boards
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
